import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { getNumber, paramKeys } from '../config/params';

const GeneralInfoSection = styled.section`
  padding: 100px 0;
  background: linear-gradient(135deg, var(--primary-blue) 0%, #1e40af 100%);
  color: white;
  position: relative;
  overflow: hidden;
  
  
  &::after {
    content: '';
    position: absolute;
    top: -50%;
    right: -20%;
    width: 80%;
    height: 200%;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.05) 0%, transparent 70%);
    animation: rotate 20s linear infinite;
  }
  
  @keyframes pulse {
    0%, 100% { transform: scale(1); opacity: 0.1; }
    50% { transform: scale(1.1); opacity: 0.15; }
  }
  
  @keyframes rotate {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 2;
`;

const ContentWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
    text-align: center;
  }
`;

const TextContent = styled.div`
  animation: slideInLeft 0.8s ease-out;
  
  @keyframes slideInLeft {
    from {
      opacity: 0;
      transform: translateX(-50px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
`;

const TechTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 2rem;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 60px;
    height: 4px;
    background: var(--primary-orange);
    border-radius: 2px;
  }
  
  @media (max-width: 768px) {
    font-size: 2rem;
    text-align: center;
    
    &::after {
      left: 50%;
      transform: translateX(-50%);
    }
  }
`;

const TechDescription = styled.p`
  font-size: 1.2rem;
  line-height: 1.8;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 2rem;
`;

const StatsContainer = styled.div`
  display: flex;
  gap: 2rem;
  margin-top: 2rem;
  
  @media (max-width: 768px) {
    justify-content: center;
    flex-wrap: wrap;
  }
`;

const StatItem = styled.div`
  text-align: center;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
  }
`;

const StatNumber = styled.div`
  /* Ensure KPI numbers are never larger than the hero subtitle text */
  font-size: clamp(1rem, 2.5vw, 1.2rem);
  font-weight: 900;
  line-height: 1.1;
  letter-spacing: 0.02em;
  color: #ffffff;
  margin-bottom: 0.35rem;
`;

const StatLabel = styled.div`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.8);
`;

const VisualContent = styled.div`
  position: relative;
  animation: slideInRight 0.8s ease-out 0.2s both;
  
  @keyframes slideInRight {
    from {
      opacity: 0;
      transform: translateX(50px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
`;

const TechIllustration = styled.div`
  width: 100%;
  height: 300px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.1), transparent);
    animation: shine 3s linear infinite;
  }
  
  @keyframes shine {
    0% { transform: translateX(-100%) translateY(-100%) rotate(45deg); }
    100% { transform: translateX(100%) translateY(100%) rotate(45deg); }
  }
  
  @media (max-width: 768px) {
    height: 200px;
  }
`;

const TechIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1rem;
  position: relative;
  z-index: 1;
`;

const TechText = styled.div`
  font-size: 1.5rem;
  font-weight: 600;
  text-align: center;
  position: relative;
  z-index: 1;
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const FloatingElements = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
  
  &::before,
  &::after {
    content: '';
    position: absolute;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 50%;
  }
  
  &::before {
    width: 100px;
    height: 100px;
    top: 20%;
    right: 15%;
    animation: float 6s ease-in-out infinite;
  }
  
  &::after {
    width: 60px;
    height: 60px;
    bottom: 30%;
    left: 20%;
    animation: float 8s ease-in-out infinite reverse;
  }
  
  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-20px); }
  }
`;

const GeneralInfo: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = React.useState(true);
  const [kpis, setKpis] = React.useState<{ years: number; founded: number; customers: number }>({ years: 20, founded: 2002, customers: 1000 });

  React.useEffect(() => {
    // All params are public and synchronous now
    const years = getNumber(paramKeys.siteYearsExperience, 10);
    const founded = getNumber(paramKeys.siteFoundedYear, 2002);
    const customers = getNumber(paramKeys.siteHappyCustomers, 1000);
    setKpis({ years, founded, customers });
    setLoading(false);
  }, []);

  const fmt = (n: number) => {
    try { return new Intl.NumberFormat('nl-NL').format(n); } catch { return String(n); }
  };

  return (
    <GeneralInfoSection id="technologies">
      <FloatingElements />
      <Container>
        <ContentWrapper>
          <TextContent>
            <TechTitle>{t('technologies.title')}</TechTitle>
            <TechDescription>{t('technologies.description')}</TechDescription>
            
            <StatsContainer>
              <StatItem>
                <StatNumber>{loading ? '—' : kpis.founded}</StatNumber>
                <StatLabel>{t('technologies.kpi.founded')}</StatLabel>
              </StatItem>
              <StatItem>
                <StatNumber>{loading ? '—' : `${kpis.years}+`}</StatNumber>
                <StatLabel>{t('technologies.kpi.years')}</StatLabel>
              </StatItem>
              <StatItem>
                <StatNumber>{loading ? '—' : `${fmt(kpis.customers)}+`}</StatNumber>
                <StatLabel>{t('technologies.kpi.happy_customers')}</StatLabel>
              </StatItem>
            </StatsContainer>
          </TextContent>
          
          <VisualContent>
            <TechIllustration>
              <TechIcon>🏫</TechIcon>
              <TechText>{t('technologies.best_driving_school')}</TechText>
            </TechIllustration>
          </VisualContent>
        </ContentWrapper>
      </Container>
    </GeneralInfoSection>
  );
};

export default GeneralInfo;